import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/WebsiteLeads.css';

const WebsiteLeads = () => {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const statusColors = {
    new: '#3B82F6',
    contacted: '#F59E0B',
    qualified: '#8B5CF6',
    proposal: '#EC4899',
    converted: '#10B981',
    lost: '#6B7280'
  };

  const statusLabels = {
    new: 'New',
    contacted: 'Contacted',
    qualified: 'Qualified',
    proposal: 'Proposal Sent',
    converted: 'Converted',
    lost: 'Lost'
  };

  useEffect(() => {
    fetchLeads();
    fetchCounts();
  }, [filter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.get('/api/leads', { params });
      setLeads(response.data.leads);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const response = await api.get('/api/leads/counts');
      setCounts(response.data);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/api/leads/${leadId}`, { status: newStatus });
      fetchLeads();
      fetchCounts();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleFollowUp = async () => {
    if (!selectedLead) return;
    setSaving(true);
    try {
      await api.put(`/api/leads/${selectedLead.id}/follow-up`, { notes });
      fetchLeads();
      fetchCounts();
      setShowModal(false);
      setSelectedLead(null);
      setNotes('');
    } catch (error) {
      console.error('Error marking follow up:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async (leadId) => {
    if (!window.confirm('Convert this lead to a client?')) return;
    try {
      await api.post(`/api/leads/${leadId}/convert`);
      fetchLeads();
      fetchCounts();
      alert('Lead converted to client successfully!');
    } catch (error) {
      console.error('Error converting lead:', error);
      alert('Error converting lead');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.delete(`/api/leads/${leadId}`);
      fetchLeads();
      fetchCounts();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openLeadDetails = (lead) => {
    setSelectedLead(lead);
    setNotes(lead.notes || '');
    setShowModal(true);
  };

  return (
    <div className="website-leads-page">
      <div className="leads-header">
        <h1>🌐 Website Leads</h1>
        <p>Manage inquiries from your website contact form</p>
      </div>

      {/* Stats Cards */}
      <div className="leads-stats">
        <div className="stat-card" onClick={() => setFilter('all')}>
          <span className="stat-number">{counts.total || 0}</span>
          <span className="stat-label">Total Leads</span>
        </div>
        <div className="stat-card new" onClick={() => setFilter('new')}>
          <span className="stat-number">{counts.new || 0}</span>
          <span className="stat-label">New</span>
        </div>
        <div className="stat-card contacted" onClick={() => setFilter('contacted')}>
          <span className="stat-number">{counts.contacted || 0}</span>
          <span className="stat-label">Contacted</span>
        </div>
        <div className="stat-card qualified" onClick={() => setFilter('qualified')}>
          <span className="stat-number">{counts.qualified || 0}</span>
          <span className="stat-label">Qualified</span>
        </div>
        <div className="stat-card converted" onClick={() => setFilter('converted')}>
          <span className="stat-number">{counts.converted || 0}</span>
          <span className="stat-label">Converted</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="leads-filters">
        {['all', 'new', 'contacted', 'qualified', 'proposal', 'converted', 'lost'].map(status => (
          <button
            key={status}
            className={`filter-btn ${filter === status ? 'active' : ''}`}
            onClick={() => setFilter(status)}
          >
            {status === 'all' ? 'All' : statusLabels[status]}
          </button>
        ))}
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="loading">Loading leads...</div>
      ) : leads.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <h3>No leads found</h3>
          <p>{filter === 'all' ? 'Website leads will appear here when visitors submit the contact form.' : `No ${statusLabels[filter] || filter} leads.`}</p>
        </div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Service</th>
                <th>Status</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id} onClick={() => openLeadDetails(lead)}>
                  <td>
                    <strong>{lead.name}</strong>
                    {lead.followed_up_at && <span className="followed-badge">✓ Followed Up</span>}
                  </td>
                  <td>
                    <div className="contact-info">
                      <a href={`mailto:${lead.email}`} onClick={e => e.stopPropagation()}>{lead.email}</a>
                      {lead.phone && <span>{lead.phone}</span>}
                    </div>
                  </td>
                  <td>{lead.service_interested || '-'}</td>
                  <td>
                    <select
                      value={lead.status}
                      onChange={(e) => { e.stopPropagation(); handleStatusChange(lead.id, e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      style={{ borderColor: statusColors[lead.status] }}
                      className="status-select"
                    >
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>{formatDate(lead.created_at)}</td>
                  <td>
                    <div className="action-buttons" onClick={e => e.stopPropagation()}>
                      {lead.status !== 'converted' && (
                        <button className="btn-convert" onClick={() => handleConvert(lead.id)} title="Convert to Client">
                          👤
                        </button>
                      )}
                      <button className="btn-delete" onClick={() => handleDelete(lead.id)} title="Delete">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lead Details Modal */}
      {showModal && selectedLead && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Lead Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="lead-detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>{selectedLead.name}</span>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <span>{selectedLead.phone || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Service Interested</label>
                  <span>{selectedLead.service_interested || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className="status-badge" style={{ background: statusColors[selectedLead.status] }}>
                    {statusLabels[selectedLead.status]}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Received</label>
                  <span>{formatDate(selectedLead.created_at)}</span>
                </div>
              </div>

              <div className="detail-message">
                <label>Message</label>
                <p>{selectedLead.message}</p>
              </div>

              <div className="detail-notes">
                <label>Internal Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this lead..."
                  rows={4}
                />
              </div>

              {selectedLead.followed_up_at && (
                <div className="follow-up-info">
                  <span>✓ Followed up on {formatDate(selectedLead.followed_up_at)}</span>
                  {selectedLead.followed_up_by_name && <span> by {selectedLead.followed_up_by_name}</span>}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleFollowUp} disabled={saving}>
                {saving ? 'Saving...' : 'Save & Mark Followed Up'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteLeads;
